type SelectElements = {
  wrapper: HTMLElement
  trigger: HTMLButtonElement
  menu: HTMLElement
  value: HTMLElement
  input: HTMLInputElement | null
  options: HTMLButtonElement[]
}

class CustomSelect {
  elements: SelectElements

  constructor(wrapper: HTMLElement) {
    const trigger = wrapper.querySelector('[data-hg-select-trigger]') as HTMLButtonElement | null
    const menu = wrapper.querySelector('[data-hg-select-menu]') as HTMLElement | null
    const value = wrapper.querySelector('[data-hg-select-value]') as HTMLElement | null

    if (!trigger || !menu || !value) {
      throw new Error('Invalid hg-select markup')
    }

    this.elements = {
      wrapper,
      trigger,
      menu,
      value,
      input: wrapper.querySelector('[data-hg-select-input]') as HTMLInputElement | null,
      options: Array.from(wrapper.querySelectorAll('[data-hg-select-option]')) as HTMLButtonElement[],
    }
  }

  init() {
    this.elements.trigger.addEventListener('click', this.onTriggerClick)
    this.elements.trigger.addEventListener('keydown', this.onTriggerKeydown)

    this.elements.options.forEach((option) => {
      option.addEventListener('click', this.onOptionClick)
      option.addEventListener('keydown', this.onOptionKeydown)
    })
  }

  cleanup() {
    this.elements.trigger.removeEventListener('click', this.onTriggerClick)
    this.elements.trigger.removeEventListener('keydown', this.onTriggerKeydown)

    this.elements.options.forEach((option) => {
      option.removeEventListener('click', this.onOptionClick)
      option.removeEventListener('keydown', this.onOptionKeydown)
    })
  }

  close() {
    this.elements.wrapper.classList.remove('is-open')
    this.elements.trigger.setAttribute('aria-expanded', 'false')
  }

  open() {
    this.elements.wrapper.classList.add('is-open')
    this.elements.trigger.setAttribute('aria-expanded', 'true')
    this.elements.wrapper.dispatchEvent(new CustomEvent('hg-select:open', { bubbles: true }))
  }

  isOpen() {
    return this.elements.wrapper.classList.contains('is-open')
  }

  selectOption(option: HTMLButtonElement, shouldFocusTrigger = false) {
    const value = option.dataset.value || option.textContent || ''
    const label = option.dataset.label || option.textContent || ''
    const staticLabel = this.elements.wrapper.dataset.hgSelectStaticLabel

    this.elements.options.forEach((item) => {
      const isSelected = item === option
      item.classList.toggle('is-selected', isSelected)
      item.setAttribute('aria-selected', isSelected ? 'true' : 'false')
    })

    if (typeof staticLabel === 'string') {
      this.elements.value.textContent = staticLabel
    } else {
      this.elements.value.textContent = label
    }

    if (this.elements.input) {
      this.elements.input.value = value
    }

    this.elements.wrapper.dispatchEvent(new CustomEvent('hgSelectChange', {
      bubbles: true,
      detail: { value, label },
    }))

    this.close()

    if (shouldFocusTrigger) {
      this.elements.trigger.focus()
    }
  }

  getSelectedOption() {
    return this.elements.options.find((option) => option.classList.contains('is-selected'))
      || this.elements.options.find((option) => option.getAttribute('aria-selected') === 'true')
      || this.elements.options[0]
      || null
  }

  focusRelativeOption(currentOption: HTMLButtonElement, delta: number) {
    const index = this.elements.options.indexOf(currentOption)
    if (index < 0) return

    const nextIndex = (index + delta + this.elements.options.length) % this.elements.options.length
    this.elements.options[nextIndex].focus()
  }

  focusFirstOption() {
    const option = this.getSelectedOption() || this.elements.options[0]
    if (option) option.focus()
  }

  onTriggerClick = () => {
    if (this.isOpen()) {
      this.close()
    } else {
      this.open()
    }
  }

  onTriggerKeydown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      if (!this.isOpen()) this.open()
      this.focusFirstOption()
      return
    }

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      if (this.isOpen()) {
        this.close()
      } else {
        this.open()
      }
      return
    }

    if (e.key === 'Escape') {
      this.close()
    }
  }

  onOptionClick = (e: Event) => {
    const option = (e.currentTarget as HTMLButtonElement | null)
    if (!option) return
    this.selectOption(option, true)
  }

  onOptionKeydown = (e: KeyboardEvent) => {
    const option = e.currentTarget as HTMLButtonElement | null
    if (!option) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      this.focusRelativeOption(option, 1)
      return
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault()
      this.focusRelativeOption(option, -1)
      return
    }

    if (e.key === 'Home') {
      e.preventDefault()
      this.elements.options[0]?.focus()
      return
    }

    if (e.key === 'End') {
      e.preventDefault()
      this.elements.options[this.elements.options.length - 1]?.focus()
      return
    }

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      this.selectOption(option, true)
      return
    }

    if (e.key === 'Escape') {
      e.preventDefault()
      this.close()
      this.elements.trigger.focus()
      return
    }

    if (e.key === 'Tab') {
      this.close()
    }
  }
}

class CustomSelects {
  selects: CustomSelect[] = []

  init(container: Document | HTMLElement = document) {
    this.selects = []

    container.querySelectorAll('[data-hg-select]').forEach((el) => {
      try {
        const select = new CustomSelect(el as HTMLElement)
        select.init()
        this.selects.push(select)
      } catch (error) {
        // Invalid markup should not break app init.
      }
    })

    document.addEventListener('click', this.onDocumentClick)
    document.addEventListener('keydown', this.onDocumentKeydown)
    document.addEventListener('hg-select:open', this.onSelectOpen as EventListener)
  }

  update(container: Document | HTMLElement = document) {
    this.cleanup()
    this.init(container)
  }

  cleanup() {
    this.selects.forEach((select) => select.cleanup())
    this.selects = []

    document.removeEventListener('click', this.onDocumentClick)
    document.removeEventListener('keydown', this.onDocumentKeydown)
    document.removeEventListener('hg-select:open', this.onSelectOpen as EventListener)
  }

  onSelectOpen = (e: CustomEvent) => {
    const currentWrapper = e.target as HTMLElement | null

    this.selects.forEach((select) => {
      if (currentWrapper && select.elements.wrapper !== currentWrapper) {
        select.close()
      }
    })
  }

  onDocumentClick = (e: MouseEvent) => {
    const target = e.target as Node | null
    if (!target) return

    this.selects.forEach((select) => {
      if (!select.elements.wrapper.contains(target)) {
        select.close()
      }
    })
  }

  onDocumentKeydown = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return
    this.selects.forEach((select) => select.close())
  }
}

export default new CustomSelects()
